import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Grid2 } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import useValidationSchema from './_form';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import FormSelect from 'components/Form/Select/Select';
import SaveIcon from '@mui/icons-material/Save';
import { Box } from '@mui/system';
import { useToken } from 'utils/hooks/useToken';
import { getInitialObjects } from 'modules/LoggedUser/_api';
import { useAppLoader } from 'utils/hooks/useAppLoader';
import { useAppDispatch } from 'store/hooks';
import { loadInitialSettings } from 'store/reducers/appReducer';

const TenantForm: React.FC<any> = ({ setTenantDialogOpen, tenants, tenant }) => {
  const { t } = useTranslation('Bar');
  const { toggleLoader } = useAppGlobals();
  const { refreshTokenAndStore } = useToken();
  const { loadAllDependencies } = useAppLoader();
  const dispatch = useAppDispatch();

  const { TenantFormSchema } = useValidationSchema();

  const methods = useForm<any>({
    resolver: yupResolver(TenantFormSchema),
  });
  const { handleSubmit } = methods;

  const onSubmit = handleSubmit(async (values) => {
    toggleLoader();

    await refreshTokenAndStore(false, values.tenant);

    // reload all settings
    const initialObject = await getInitialObjects();
    dispatch(loadInitialSettings(initialObject));
    await loadAllDependencies();

    setTenantDialogOpen(false);
    toggleLoader(false);
  });

  useEffect(() => {
    if (tenant) {
      methods.setValue('tenant', tenant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <Grid2 container={true} spacing={0}>
            <Grid2 size={12}>
              <FormSelect
                name="tenant"
                label={t('tenant')}
                required={true}
                items={tenants.map((item: any) => ({ id: item.code, label: item.name }))}
              />
            </Grid2>

            <Grid2 size={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  displayPrint: 'none',
                }}
              >
                <Button
                  onClick={() => setTenantDialogOpen(false)}
                  variant="contained"
                  color="inherit"
                  size="large"
                  sx={{ mr: 1 }}
                >
                  {t('Form:close')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  size="large"
                  data-pw="save-users-and-permissions"
                >
                  <SaveIcon sx={{ mr: 1 }} />
                  {t('Form:confirm')}
                </Button>
              </Box>
            </Grid2>
          </Grid2>
        </form>
      </FormProvider>
    </>
  );
};

export default TenantForm;
